/**
 * Body of the Admin -> Plans "Sync from pricing sheet" confirm dialog: the
 * server's dry run (`GET /billing/admin/plans/sync-defaults/preview`) of what
 * `POST /billing/admin/plans/sync-defaults` would write on this stack.
 */
import React from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { InlineMessage, ListSkeleton, StatusChip } from '../ui';
import { tokens } from '../../theme/tokens';

export interface PlanSyncLimitDiff {
  key: string;
  stored: unknown;
  default: unknown;
  presence: 'absent' | 'different';
}

export interface PlanSyncFieldChange {
  field: string;
  stored: unknown;
  default: unknown;
}

export interface PlanSyncPreviewEntry {
  planName: string;
  tier: string | null;
  storedId: string | null;
  storedName: string | null;
  action: 'create' | 'update' | 'unchanged';
  fieldChanges: PlanSyncFieldChange[];
  limitDiffs: PlanSyncLimitDiff[];
}

export interface PlanSyncPreviewResult {
  stack: { projectId: string; billingRegion: 'US' | 'CA'; currency: string };
  plans: PlanSyncPreviewEntry[];
  retired: Array<{ id: string; name: string; alreadyInactive: boolean }>;
  skipped: Array<{ id: string; name: string }>;
  changeCount: number;
}

const FIELD_LABELS: Record<string, string> = {
  name: 'Name',
  description: 'Description',
  whoItsFor: "Who it's for",
  features: 'Feature bullets',
  price: 'Price',
  currency: 'Currency',
  interval: 'Billing interval',
  setupFee: 'Setup fee',
  setupFeeCustom: 'Quoted setup fee',
};

export const formatSyncValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return 'none';
  if (typeof value === 'boolean') return value ? 'on' : 'off';
  if (value === -1) return 'unlimited';
  if (Array.isArray(value)) return `${value.length} item${value.length === 1 ? '' : 's'}`;
  if (typeof value === 'object') return JSON.stringify(value);
  const text = String(value);
  return text.length > 60 ? `${text.slice(0, 57)}…` : text;
};

export const stackLabel = (stack: PlanSyncPreviewResult['stack']): string =>
  `${stack.billingRegion === 'CA' ? 'Canada' : 'US'} stack`;

const ChangeLine: React.FC<{ label: React.ReactNode; from: unknown; to: unknown; note?: string }> = ({ label, from, to, note }) => (
  <Typography variant="body2" sx={{ fontSize: 12.5, color: tokens.ink.body }}>
    {label}: {formatSyncValue(from)} → <strong>{formatSyncValue(to)}</strong>
    {note && (
      <Box component="span" sx={{ color: tokens.signal.caution.ink }}>
        {' '}
        ({note})
      </Box>
    )}
  </Typography>
);

interface PlanSyncPreviewProps {
  preview: PlanSyncPreviewResult | undefined;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
}

export const PlanSyncPreview: React.FC<PlanSyncPreviewProps> = ({ preview, loading, error, onRetry }) => {
  if (loading) return <ListSkeleton rows={4} />;
  if (error) {
    return (
      <Box sx={{ mt: 2 }}>
        <InlineMessage severity="error">Could not load the preview: {error}</InlineMessage>
        <Button size="small" onClick={onRetry}>
          Try again
        </Button>
      </Box>
    );
  }
  if (!preview) return null;

  const created = preview.plans.filter(p => p.action === 'create');
  const updated = preview.plans.filter(p => p.action === 'update');
  const unchanged = preview.plans.filter(p => p.action === 'unchanged');
  const retiring = preview.retired.filter(r => !r.alreadyInactive);
  const summary = [
    `${created.length} created`,
    `${updated.length} updated`,
    `${retiring.length} retired`,
    `${unchanged.length} unchanged`,
    preview.skipped.length ? `${preview.skipped.length} custom left alone` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Stack spacing={1.5} sx={{ mt: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <StatusChip tone="info" label={stackLabel(preview.stack)} />
        <Typography sx={{ fontSize: 12, fontFamily: tokens.font.mono, color: tokens.ink.bodyAlt }}>
          {preview.stack.projectId}
        </Typography>
        <Typography variant="body2" sx={{ fontSize: 12, color: 'text.secondary' }}>
          Prices in {preview.stack.currency}
        </Typography>
      </Stack>
      <Typography variant="body2" sx={{ fontSize: 12, color: 'text.secondary' }}>
        Only this stack&apos;s plans are written. Run the sync again from the other stack&apos;s admin to roll it out there.
      </Typography>

      {preview.changeCount === 0 ? (
        <InlineMessage severity="success">Every plan already matches the pricing sheet. Nothing to sync.</InlineMessage>
      ) : (
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {preview.changeCount} change{preview.changeCount === 1 ? '' : 's'}: {summary}
        </Typography>
      )}

      {(created.length > 0 || updated.length > 0 || retiring.length > 0) && (
        <Box
          data-testid="plan-sync-changes"
          sx={{ maxHeight: 340, overflowY: 'auto', border: `1px solid ${tokens.line.default}`, borderRadius: 1 }}
        >
          {[...created, ...updated].map(entry => (
            <Box key={entry.planName} sx={{ px: 1.5, py: 1.25, borderBottom: `1px solid ${tokens.line.hairline}` }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {entry.planName}
                </Typography>
                {entry.storedName && entry.storedName !== entry.planName && (
                  <Typography variant="body2" sx={{ fontSize: 12, color: 'text.secondary' }}>
                    was {entry.storedName}
                  </Typography>
                )}
                <StatusChip tone={entry.action === 'create' ? 'positive' : 'caution'} label={entry.action === 'create' ? 'New plan' : 'Update'} />
              </Stack>
              {entry.action === 'create' && (
                <Typography variant="body2" sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                  No stored plan yet. It is created from the pricing sheet with no Stripe price linked.
                </Typography>
              )}
              {entry.fieldChanges.map(change => (
                <ChangeLine key={change.field} label={FIELD_LABELS[change.field] || change.field} from={change.stored} to={change.default} />
              ))}
              {entry.limitDiffs.map(diff => (
                <ChangeLine
                  key={diff.key}
                  label={
                    <Box component="span" sx={{ fontFamily: tokens.font.mono, fontSize: 12 }}>
                      {diff.key}
                    </Box>
                  }
                  from={diff.stored}
                  to={diff.default}
                  note={diff.presence === 'absent' ? 'missing on the stored plan' : undefined}
                />
              ))}
            </Box>
          ))}
          {retiring.map(r => (
            <Box key={r.id} sx={{ px: 1.5, py: 1.25, borderBottom: `1px solid ${tokens.line.hairline}` }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {r.name}
                </Typography>
                <StatusChip tone="neutral" label="Retire" />
              </Stack>
              <Typography variant="body2" sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                Legacy plan beside its replacement. Hidden from stores; its Stripe price and subscribers are untouched.
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {unchanged.length > 0 && preview.changeCount > 0 && (
        <Typography variant="body2" sx={{ fontSize: 12, color: 'text.secondary' }}>
          Already in sync: {unchanged.map(p => p.planName).join(', ')}
        </Typography>
      )}
      {preview.skipped.length > 0 && (
        <Typography variant="body2" sx={{ fontSize: 12, color: 'text.secondary' }}>
          Custom plans left alone: {preview.skipped.map(s => s.name).join(', ')}
        </Typography>
      )}
    </Stack>
  );
};
